import { useState } from "react";
import type { DramaFilter } from "@/types/drama";
import { GENRES, REGIONS, YEARS } from "@/lib/drama";

interface FilterSidebarProps {
  filter: DramaFilter;
  onChange: (key: keyof DramaFilter, value: string) => void;
  onReset: () => void;
  resultCount: number;
}

const GROUPS: { key: keyof DramaFilter; label: string; options: string[]; icon: string }[] = [
  { key: "genre", label: "类型", options: GENRES, icon: "ri-price-tag-3-line" },
  { key: "region", label: "地区", options: REGIONS, icon: "ri-earth-line" },
  { key: "year", label: "年份", options: YEARS, icon: "ri-calendar-line" },
];

export default function FilterSidebar({ filter, onChange, onReset, resultCount }: FilterSidebarProps) {
  const [open, setOpen] = useState(false);

  const activeCount = GROUPS.filter((group) => filter[group.key] !== "全部").length;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center justify-between px-4 py-3 lg:cursor-default"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-foreground-900">
          <i className="ri-filter-3-line text-base text-primary-600" aria-hidden="true" />
          筛选条件
          {activeCount > 0 && (
            <span className="rounded-full bg-accent-500 px-1.5 text-[10px] font-semibold text-primary-950">
              {activeCount}
            </span>
          )}
        </span>
        <i
          className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform lg:hidden ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>

      <div className={`${open ? "block" : "hidden"} px-4 pb-4 lg:block lg:pt-1`}>
        {GROUPS.map((group, index) => (
          <div key={group.key} className={index > 0 ? "mt-5 border-t border-background-200 pt-4" : ""}>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-foreground-500">
              <i className={`${group.icon} text-sm`} aria-hidden="true" />
              {group.label}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {group.options.map((option) => {
                const active = filter[group.key] === option;
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => onChange(group.key, option)}
                    className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs transition-colors ${
                      active
                        ? "bg-primary-500 font-medium text-background-50"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="mt-5 flex items-center justify-between border-t border-background-200 pt-4">
          <p className="text-xs text-foreground-500">
            共 <span className="font-semibold text-primary-700">{resultCount}</span> 部
          </p>
          <button
            type="button"
            onClick={onReset}
            className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-primary-600 transition-colors hover:text-primary-800"
          >
            <i className="ri-refresh-line" aria-hidden="true" />
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}